/** 플랫폼 EVT/GRP 이용권 코드 · Supabase */

import { supabase } from '../supabaseClient';
import type {
  AdminStoredEventPassRow,
  AdminStoredGroupPassRow,
} from '../utils/adminIssuedPassCodes';

function mapEvent(row: Record<string, unknown>): AdminStoredEventPassRow {
  const activeUserCount =
    typeof row.active_user_count === 'number' && Number.isFinite(row.active_user_count)
      ? Math.floor(row.active_user_count)
      : 1;
  const activatedUserCount = Math.min(
    activeUserCount,
    typeof row.activated_user_count === 'number' && Number.isFinite(row.activated_user_count)
      ? Math.floor(row.activated_user_count)
      : 0,
  );
  return {
    id: String(row.id),
    code: String(row.code),
    issuedAt: typeof row.issued_at === 'string' ? row.issued_at : new Date().toISOString(),
    eventName: typeof row.event_name === 'string' ? row.event_name : '(이름 없음)',
    paidPlanStartMonth:
      typeof row.paid_plan_start_month === 'string' ? row.paid_plan_start_month : '',
    durationMonths:
      typeof row.duration_months === 'number' && Number.isFinite(row.duration_months)
        ? Math.floor(row.duration_months)
        : 1,
    planEndIso:
      typeof row.plan_end_iso === 'string'
        ? row.plan_end_iso.slice(0, 10)
        : String(row.plan_end_iso ?? '').slice(0, 10),
    activeUserCount,
    activatedUserCount,
    saleStatus: row.sale_status === 'closed' ? 'closed' : 'active',
  };
}

function mapGroup(row: Record<string, unknown>): AdminStoredGroupPassRow {
  const userCount =
    typeof row.user_count === 'number' && Number.isFinite(row.user_count)
      ? Math.floor(row.user_count)
      : 1;
  const activatedUserCount = Math.min(
    userCount,
    typeof row.activated_user_count === 'number' && Number.isFinite(row.activated_user_count)
      ? Math.floor(row.activated_user_count)
      : 0,
  );
  return {
    id: String(row.id),
    code: String(row.code),
    issuedAt: typeof row.issued_at === 'string' ? row.issued_at : new Date().toISOString(),
    groupName: typeof row.group_name === 'string' ? row.group_name : '(이름 없음)',
    contactEmail: typeof row.contact_email === 'string' ? row.contact_email : '',
    paidPlanStartDate:
      typeof row.paid_plan_start_date === 'string'
        ? row.paid_plan_start_date.slice(0, 10)
        : String(row.paid_plan_start_date ?? '').slice(0, 10),
    userCount,
    activatedUserCount,
    orderStatus: row.order_status === 'refunded' ? 'refunded' : 'issued',
    refundedAt: typeof row.refunded_at === 'string' ? row.refunded_at : null,
    lastResentAt: typeof row.last_resent_at === 'string' ? row.last_resent_at : null,
  };
}

function eventToInsert(r: AdminStoredEventPassRow): Record<string, unknown> {
  return {
    id: r.id,
    code: r.code,
    event_name: r.eventName,
    paid_plan_start_month: r.paidPlanStartMonth,
    duration_months: r.durationMonths,
    plan_end_iso: r.planEndIso,
    active_user_count: r.activeUserCount,
    activated_user_count: r.activatedUserCount,
    sale_status: r.saleStatus,
    issued_at: r.issuedAt,
    updated_at: new Date().toISOString(),
  };
}

function groupToInsert(r: AdminStoredGroupPassRow): Record<string, unknown> {
  return {
    id: r.id,
    code: r.code,
    group_name: r.groupName,
    contact_email: r.contactEmail,
    paid_plan_start_date: r.paidPlanStartDate,
    user_count: r.userCount,
    activated_user_count: r.activatedUserCount,
    order_status: r.orderStatus,
    issued_at: r.issuedAt,
    refunded_at: r.refundedAt ?? null,
    last_resent_at: r.lastResentAt ?? null,
    updated_at: new Date().toISOString(),
  };
}

export async function fetchEventPassCodesAdmin(): Promise<AdminStoredEventPassRow[]> {
  const { data, error } = await supabase
    .from('platform_event_pass_codes')
    .select('*')
    .order('issued_at', { ascending: false })
    .limit(500);
  if (error) throw error;
  return (data ?? []).map((r) => mapEvent(r as Record<string, unknown>));
}

export async function fetchGroupPassCodesAdmin(): Promise<AdminStoredGroupPassRow[]> {
  const { data, error } = await supabase
    .from('platform_group_pass_codes')
    .select('*')
    .order('issued_at', { ascending: false })
    .limit(500);
  if (error) throw error;
  return (data ?? []).map((r) => mapGroup(r as Record<string, unknown>));
}

export async function insertEventPassCode(
  row: AdminStoredEventPassRow,
): Promise<AdminStoredEventPassRow> {
  const { data, error } = await supabase
    .from('platform_event_pass_codes')
    .insert(eventToInsert(row))
    .select('*')
    .single();
  if (error) throw error;
  return mapEvent(data as Record<string, unknown>);
}

export async function insertGroupPassCode(
  row: AdminStoredGroupPassRow,
): Promise<AdminStoredGroupPassRow> {
  const { data, error } = await supabase
    .from('platform_group_pass_codes')
    .insert(groupToInsert(row))
    .select('*')
    .single();
  if (error) throw error;
  return mapGroup(data as Record<string, unknown>);
}

export async function updateEventPassCode(
  id: string,
  patch: Partial<Omit<AdminStoredEventPassRow, 'id'>>,
): Promise<AdminStoredEventPassRow> {
  const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.code !== undefined) row.code = patch.code;
  if (patch.eventName !== undefined) row.event_name = patch.eventName;
  if (patch.paidPlanStartMonth !== undefined) row.paid_plan_start_month = patch.paidPlanStartMonth;
  if (patch.durationMonths !== undefined) row.duration_months = patch.durationMonths;
  if (patch.planEndIso !== undefined) row.plan_end_iso = patch.planEndIso;
  if (patch.activeUserCount !== undefined) row.active_user_count = patch.activeUserCount;
  if (patch.activatedUserCount !== undefined) row.activated_user_count = patch.activatedUserCount;
  if (patch.saleStatus !== undefined) row.sale_status = patch.saleStatus;
  if (patch.issuedAt !== undefined) row.issued_at = patch.issuedAt;

  const { data, error } = await supabase
    .from('platform_event_pass_codes')
    .update(row)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return mapEvent(data as Record<string, unknown>);
}

export async function updateGroupPassCode(
  id: string,
  patch: Partial<Omit<AdminStoredGroupPassRow, 'id'>>,
): Promise<AdminStoredGroupPassRow> {
  const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.code !== undefined) row.code = patch.code;
  if (patch.groupName !== undefined) row.group_name = patch.groupName;
  if (patch.contactEmail !== undefined) row.contact_email = patch.contactEmail;
  if (patch.paidPlanStartDate !== undefined) row.paid_plan_start_date = patch.paidPlanStartDate;
  if (patch.userCount !== undefined) row.user_count = patch.userCount;
  if (patch.activatedUserCount !== undefined) row.activated_user_count = patch.activatedUserCount;
  if (patch.orderStatus !== undefined) row.order_status = patch.orderStatus;
  if (patch.refundedAt !== undefined) row.refunded_at = patch.refundedAt;
  if (patch.lastResentAt !== undefined) row.last_resent_at = patch.lastResentAt;
  if (patch.issuedAt !== undefined) row.issued_at = patch.issuedAt;

  const { data, error } = await supabase
    .from('platform_group_pass_codes')
    .update(row)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return mapGroup(data as Record<string, unknown>);
}

export async function deleteEventPassCodeRemote(id: string): Promise<void> {
  const { error } = await supabase.from('platform_event_pass_codes').delete().eq('id', id);
  if (error) throw error;
}

export async function deleteGroupPassCodeRemote(id: string): Promise<void> {
  const { error } = await supabase.from('platform_group_pass_codes').delete().eq('id', id);
  if (error) throw error;
}

export type RedeemEventPassCodeResult = {
  id: string;
  code: string;
  event_name: string;
  paid_plan_start_month: string;
  duration_months: number;
  plan_end_iso: string;
  active_user_count: number;
  activated_user_count: number;
};

export type RedeemGroupPassCodeResult = {
  id: string;
  code: string;
  group_name: string;
  contact_email: string;
  paid_plan_start_date: string;
  period_end_iso: string;
  user_count: number;
  activated_user_count: number;
};

export async function redeemEventPassCodeRemote(code: string): Promise<RedeemEventPassCodeResult> {
  const { data, error } = await supabase.rpc('redeem_platform_event_pass_code', { p_code: code });
  if (error) throw error;
  return data as RedeemEventPassCodeResult;
}

export async function redeemGroupPassCodeRemote(code: string): Promise<RedeemGroupPassCodeResult> {
  const { data, error } = await supabase.rpc('redeem_platform_group_pass_code', { p_code: code });
  if (error) throw error;
  return data as RedeemGroupPassCodeResult;
}

/** 로그인 이용자: 그룹 구매 시 GRP- 코드를 DB에 발급 */
export async function issueGroupPassCodeRemote(input: {
  id?: string;
  code: string;
  groupName: string;
  contactEmail: string;
  paidPlanStartDate: string;
  userCount: number;
}): Promise<AdminStoredGroupPassRow> {
  const { data, error } = await supabase.rpc('issue_platform_group_pass_code', {
    p_code: input.code,
    p_group_name: input.groupName,
    p_contact_email: input.contactEmail,
    p_paid_plan_start_date: input.paidPlanStartDate,
    p_user_count: input.userCount,
    p_id: input.id ?? null,
  });
  if (error) throw error;
  return mapGroup(data as Record<string, unknown>);
}

export function isPassCodeRedeemExhaustedError(err: unknown): boolean {
  const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : String(err ?? '');
  return /INVALID_OR_EXHAUSTED|SOLD_OUT_OR_CLOSED|NOT_AUTHENTICATED/i.test(msg);
}

/** 서버(RPC): 이미 이용 중인 이용권이 있어 새 이용권을 적용할 수 없음 */
export function isActivePassExistsError(err: unknown): boolean {
  const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : String(err ?? '');
  return /ACTIVE_PASS_EXISTS/.test(msg);
}

/** 이용권 중복 적용 금지 안내 문구 */
export function activePassBlockedMessage(passLabel: string): string {
  return `같은 학년도 이용권이 이미 있어 ${passLabel}을 등록할 수 없습니다.\n다음 학년도 이용권은 이용권 연도가 겹치는 2월부터 구매·등록할 수 있습니다.`;
}

export async function importEventPassCodesFromLocalIfEmpty(
  localRows: AdminStoredEventPassRow[],
): Promise<AdminStoredEventPassRow[]> {
  const existing = await fetchEventPassCodesAdmin();
  if (existing.length > 0 || localRows.length === 0) return existing;
  const { error } = await supabase
    .from('platform_event_pass_codes')
    .insert(localRows.map(eventToInsert));
  if (error) throw error;
  return fetchEventPassCodesAdmin();
}

export async function importGroupPassCodesFromLocalIfEmpty(
  localRows: AdminStoredGroupPassRow[],
): Promise<AdminStoredGroupPassRow[]> {
  const existing = await fetchGroupPassCodesAdmin();
  if (existing.length > 0 || localRows.length === 0) return existing;
  const { error } = await supabase
    .from('platform_group_pass_codes')
    .insert(localRows.map(groupToInsert));
  if (error) throw error;
  return fetchGroupPassCodesAdmin();
}
